import { useState } from "react";
import { Redirect, useLocalSearchParams, useRouter } from "expo-router";
import { standardSchemaResolver } from "@hookform/resolvers/standard-schema";
import { CheckIcon } from "lucide-react-native";
import { useForm } from "react-hook-form";

import { BackButton } from "@/blocks/BackButton";
import { ScreenLayout } from "@/blocks/ScreenLayout";
import { SecureTextEntryToggle } from "@/blocks/SecureTextEntryToggle";
import { VnBox } from "@/components/VnBox";
import { VnButton } from "@/components/VnButton/VnButton";
import { VnCard } from "@/components/VnCard";
import { VnForm } from "@/components/VnForm";
import { VnIcon } from "@/components/VnIcon";
import { VnText } from "@/components/VnText/VnText";
import { VnTextInput } from "@/components/VnTextInput/VnTextInput";
import {
  useCreateUsernameAccountMutation,
  useSignInWithEmailAndPasswordMutation,
} from "@/data/mutations/authMutations";
import {
  createPasswordFormSchema,
  CreatePasswordFormType,
  usernameSchema,
} from "@/domain/auth";
import { useTranslator } from "@/hooks/useTranslate";
import { logger } from "@/utils/logger";
import { normalizeSearchParam } from "@/utils/routing";
import { getUsernameAuthEmail } from "@/utils/usernameAuth";

const CreateUsernameAccountScreen = () => {
  const { t } = useTranslator();
  const router = useRouter();
  const { username: usernameParam } = useLocalSearchParams<{
    username?: string;
  }>();
  const parsedUsername = usernameSchema.safeParse(
    normalizeSearchParam(usernameParam),
  );
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);

  const { mutateAsync: createUsernameAccount, isPending: isCreatingAccount } =
    useCreateUsernameAccountMutation();
  const { mutate: signIn, isPending: isSigningIn } =
    useSignInWithEmailAndPasswordMutation();

  const form = useForm<CreatePasswordFormType>({
    defaultValues: { password: "" },
    resolver: standardSchemaResolver(createPasswordFormSchema),
  });

  const isPasswordValid = createPasswordFormSchema.safeParse({
    password: form.watch("password"),
  }).success;

  if (!parsedUsername.success) {
    return <Redirect href="/(unauthed)/auth" />;
  }

  const username = parsedUsername.data;

  const handleSubmit = async ({ password }: CreatePasswordFormType) => {
    let isUsernameTaken = false;

    try {
      ({ isUsernameTaken } = await createUsernameAccount({
        username,
        password,
      }));
    } catch (error) {
      logger.error({
        message: "Failed to create a username account",
        error,
      });
      form.setError("password", {
        message: t("common:validation.genericError"),
      });
      return;
    }

    if (isUsernameTaken) {
      router.replace({ pathname: "/password", params: { username } });
      return;
    }

    signIn(
      { email: getUsernameAuthEmail(username), password },
      {
        onError: error => {
          logger.error({
            message: "Failed to sign in after creating a username account",
            error,
          });
          router.replace({ pathname: "/password", params: { username } });
        },
      },
    );
  };

  return (
    <ScreenLayout className="py-12">
      <VnBox className="mt-4 items-start">
        <BackButton
          textClassName="text-sidebar"
          iconClassName="text-sidebar"
          onPress={() => router.back()}
          variant="ghost"
        />
      </VnBox>
      <VnBox className="mt-safe-offset-10 web:mt-10 flex items-center px-4 sm:my-auto md:justify-center">
        <VnBox className="w-full gap-4 md:w-3/6">
          <VnBox className="flex flex-col gap-6">
            <VnBox className="gap-2">
              <VnText
                variant="headerOne"
                i18nKey="native:createUsernameAccountScreen.title"
                className="text-center"
              />
              <VnText
                variant="lg"
                className="text-muted-foreground text-center"
                i18nKey="native:createUsernameAccountScreen.subtitle"
              />
            </VnBox>
            <VnForm form={form} isInputOnlyForm>
              <VnCard className="gap-6 rounded-2xl px-4 py-[20px]">
                <VnBox className="gap-3">
                  <VnTextInput
                    control={form.control}
                    name="password"
                    labelI18nKey="native:createUsernameAccountScreen.passwordInputLabel"
                    placeholder={t(
                      "native:createUsernameAccountScreen.passwordInputPlaceholder",
                    )}
                    autoCapitalize="none"
                    autoCorrect={false}
                    autoFocus
                    textContentType="newPassword"
                    autoComplete="new-password"
                    secureTextEntry={!isPasswordVisible}
                    returnKeyType="done"
                    enablesReturnKeyAutomatically
                    onSubmitEditing={() => {
                      void form.handleSubmit(handleSubmit)();
                    }}
                    isRequired
                    rightElement={
                      <SecureTextEntryToggle
                        isVisible={isPasswordVisible}
                        onPress={() => setIsPasswordVisible(prev => !prev)}
                      />
                    }
                  />
                  <VnBox className="flex-row items-center gap-2 px-1">
                    {isPasswordValid && (
                      <VnIcon
                        as={CheckIcon}
                        size={16}
                        className="text-green-600"
                      />
                    )}
                    <VnText
                      variant="sm"
                      className={
                        isPasswordValid
                          ? "text-green-600"
                          : "text-muted-foreground"
                      }
                      i18nKey="native:createPasswordScreen.passwordRequirement"
                    />
                  </VnBox>
                </VnBox>
                <VnButton
                  onPress={() => {
                    void form.handleSubmit(handleSubmit)();
                  }}
                  i18nKey="common:continue"
                  isLoading={isCreatingAccount || isSigningIn}
                  className="rounded-xl"
                  disabled={!isPasswordValid}
                />
              </VnCard>
            </VnForm>
          </VnBox>
        </VnBox>
      </VnBox>
    </ScreenLayout>
  );
};

export { CreateUsernameAccountScreen };
